import React, { useState, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import TenderProcessTable from "../TenderProcessTable/TenderProcessTable";
import SubStepTracker from "../../components/SubStepTracker/SubStepTracker";
import SaveAsDropBox from "../SaveAsDropBox/SaveAsDropBox";
import SelectInsurerDropBox from "../SelectInsurerDropBox/SelectInsurerDropBox";
import { 
    importCurrentPolicy,
    processClientComparison,
    exportClientComparison,
    reselectClientPolicy,
} from "../../api/api";
import { 
    ImportButton,
    Header,
    SubHeader,
    SourceDocumentsPageContainer,
    GenerateButton,
    ReselectButton,
    ButtonContainer
} from "../TenderProcess/TenderProcess.styles";
import { useTenderFiles } from "./useTenderFiles";
import { useToast } from '../ToastProvider/ToastProvider';


const UploadSourceDocuments = ({ handleFileUpload }) => {
    const fileInputRef = useRef(null);

    const handleImportClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        if (files.length > 0) {
            handleFileUpload(files);
        }
    };

    return (
        <>
            <ImportButton data-testid="import-button" onClick={handleImportClick}>
                Import
            </ImportButton>
            <input
                data-testid="file-selector"
                ref={fileInputRef}
                type="file"
                multiple
                accept=".docx, .pdf, .xlsx"
                style={{ display: "none" }}
                onChange={handleFileChange}
            />
        </>
    );
};

const ReviewFiles = ({ handleReselectFiles, handleProcessSpreadsheet, files, onInsurerChange }) => {
    return (
        <>
            <TenderProcessTable files={files}/>
            <SelectInsurerDropBox onInsurerChange={onInsurerChange}/>
            <ButtonContainer>
                <ReselectButton data-testid="reselect-button" onClick={handleReselectFiles}>Reselect Files</ReselectButton>
                <GenerateButton data-testid="generate-button" onClick={handleProcessSpreadsheet}>Generate</GenerateButton>
            </ButtonContainer>
        </>
    );
};

const SaveSummarySpreadsheet = ({ handleExportSpreadsheet }) => {
    return (
        <>
            <SaveAsDropBox />
            <ImportButton data-testid="save-to-sharepoint-button" onClick={handleExportSpreadsheet}>
                Save File
            </ImportButton>
        </>
    );
};

const mapSubSteptoNumber = (subStep) => {
    if (subStep == null) return 1;
    return {
        'upload': 1,
        'process': 2,
        'generate': 3
    }[subStep]
}

const ClientComparison = ({tenderId, subStep}) => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [step, setStep] = useState(mapSubSteptoNumber(subStep));
    const [loading, setLoading] = useState(false);
    const [insurers, setInsurers] = useState(['bryte']);
    const { files, setFiles } = useTenderFiles({ tenderId, step, setLoading });

    const onInsurerChange = (selectedInsurers) => {
        setInsurers(selectedInsurers);
    }

    const handleFileUpload = async (selectedFiles) => {
        setLoading(true);
        try {
            const result =  await importCurrentPolicy(tenderId, selectedFiles);
            const data = await result.json();
            setFiles(data.files);
            setStep(2);
        } catch (error) {
            if (error.isAuthExpired) return;
            console.error("An error occurred during file upload:", error);
            showToast('Ensure correct documents are selected.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleReselectFiles = async () => {
        setLoading(true);
        try {
            await reselectClientPolicy(tenderId);
            setFiles([]);
            setStep(1);
        } catch (error) {
            if (error.isAuthExpired) return;
            console.error("An error occurred during file reselect:", error);
            showToast('Something went wrong, please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleProcessSpreadsheet = async () => {
        setLoading(true);
        try {
            await processClientComparison(tenderId, insurers);
            setStep(3);
        } catch (error) {
            if (error.isAuthExpired) return;
            console.error("An error occurred during spreadsheet processing:", error);
            // Use the error message from the API, or fallback to generic message
            const errorMessage = error.message || 'An error occurred. Please check documents.';
            showToast(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleExportSpreadsheet = async () => {
        setLoading(true);        
        try {
            await exportClientComparison(tenderId);
            navigate(`/tender-listing`);
            showToast('Tender completed successfully!', 'success');
        } catch (error) {
            if (error.isAuthExpired) return;
            console.error("An error occurred during spreadsheet export:", error);
            showToast('An error occurred during spreadsheet export.', 'error');
        } finally {
            setLoading(false);
        }
    }

    const stepMappings = {
        1: {
            title: "Upload Client Current Insurer Policy",
            subtitle: "We'll use info from the summary breakdown for the selected insurer",
            component: <UploadSourceDocuments handleFileUpload={handleFileUpload} />,
        },
        2: {
            title: "Upload Complete",
            subtitle: "Ensure the files are correct and select an insurer to compare.",
            component: <ReviewFiles 
                handleReselectFiles={handleReselectFiles} 
                handleProcessSpreadsheet={handleProcessSpreadsheet} 
                files={files}
                onInsurerChange={onInsurerChange} 
            />,
        },
        3: {
            title: "Export to Sharepoint",
            subtitle: "Your Quote Client Comparison is ready to be exported",
            component: <SaveSummarySpreadsheet handleExportSpreadsheet={handleExportSpreadsheet} />,
        },
    };

    return (
        <SourceDocumentsPageContainer step={step}>
            <SubStepTracker currentStep={step} />
            <Header data-testid="tenderHeader">{stepMappings[step].title}</Header>
            <SubHeader data-testid="tenderSubheader">{stepMappings[step].subtitle}</SubHeader>
            {loading ? (
                <img src="/Icons/jigsaw-loading.gif" alt="Loading" data-testid="loading-screen" />
            ) : (
                stepMappings[step].component
            )}
        </SourceDocumentsPageContainer>
    );
};

export default ClientComparison;